import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { products } from '../data/content';

const ProductDetail = () => {
  const { productId } = useParams();
  const product = products.find(p => p.id === productId);

  // Scroll to top when component mounts or productId changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [productId]);

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-semibold mb-4">Product Not Found</h1>
        <Link to="/products">
          <Button>Back to Products</Button>
        </Link>
      </div>
    );
  }

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-4 lg:px-8 py-4">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span>/</span>
            <Link to="/products" className="hover:text-foreground">Products</Link>
            <span>/</span>
            <span className="text-foreground">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Product Detail */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <Link to="/products" className="inline-flex items-center space-x-2 text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Products</span>
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image */}
            <motion.div {...fadeInUp}>
              <div className="rounded-2xl overflow-hidden shadow-lg sticky top-24">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-full h-auto"
                />
              </div>
            </motion.div>

            {/* Details */}
            <motion.div className="space-y-6" {...fadeInUp} transition={{ delay: 0.2 }}>
              <div>
                <Badge className="mb-4">{product.category}</Badge>
                <h1 className="text-3xl sm:text-4xl font-semibold mb-4 text-[#ed3237]">{product.name}</h1>
                <p className="text-lg text-muted-foreground">{product.description}</p>
              </div>

              {/* Quick Specs */}
              <div className="flex flex-wrap gap-2">
                {product.specs.map((spec, i) => (
                  <Badge key={i} variant="outline">
                    {spec}
                  </Badge>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/contact" className="flex-1">
                  <Button size="lg" className="w-full" data-testid="product-get-quote-button">
                    Request a Quote
                  </Button>
                </Link>
                <a 
                  href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER?.replace(/[^0-9]/g, '') || '917073339922'}?text=${encodeURIComponent(`Hi, I'm interested in ${product.name}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button size="lg" variant="outline" className="w-full">
                    WhatsApp Us
                  </Button>
                </a>
              </div>

              {/* Technical Specifications */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#ed3237]">Technical Specifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="space-y-4">
                    {Object.entries(product.technicalSpecs).map(([key, value], idx) => (
                      <div key={idx} className="grid grid-cols-3 gap-4">
                        <dt className="font-medium text-sm col-span-1">{key}</dt>
                        <dd className="text-sm text-muted-foreground col-span-2">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </CardContent>
              </Card>

              {/* Applications */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#ed3237]">Applications</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {product.applications.map((app, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <CheckCircle2 className="h-5 w-5 text-[#ed3237] mt-0.5 flex-shrink-0" />
                        <span>{app}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
